<template>
  <div ref="root" class="relative">
    <button ref="trigger" type="button" :id="id" aria-haspopup="listbox" :aria-expanded="open" :aria-label="`${label}: ${selectedLabel}`"
      @click="toggle" @keydown="onTriggerKey"
      class="inline-flex items-center gap-1 h-8 pl-2.5 pr-1.5 rounded-lg border text-[13px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
      :class="active
        ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'">
      <span v-if="!active" class="text-gray-500 dark:text-gray-400">{{ label }}</span>
      <span v-else class="max-w-[10rem] truncate">{{ selectedLabel }}</span>
      <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform" :class="[open ? 'rotate-180' : '', active ? 'text-blue-400' : 'text-gray-400']"
        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
      </svg>
    </button>

    <Transition enter-active-class="transition duration-100 ease-out" enter-from-class="opacity-0 -translate-y-1" enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-75 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
      <ul v-if="open" ref="menu" role="listbox" tabindex="-1" :aria-labelledby="id" :aria-activedescendant="`${id}-option-${highlighted}`"
        @keydown="onMenuKey"
        class="absolute left-0 z-40 mt-1.5 min-w-[14rem] max-w-[20rem] max-h-72 overflow-y-auto py-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-lg focus:outline-none">
        <li v-for="(option, index) in options" :key="option.value" :id="`${id}-option-${index}`" role="option" :aria-selected="option.value === modelValue"
          @click="choose(option)" @mousemove="highlighted = index"
          class="flex items-center justify-between gap-3 px-3 py-1.5 text-[13px] cursor-pointer"
          :class="[
            index === highlighted ? 'bg-gray-50 dark:bg-gray-800' : '',
            option.value === modelValue ? 'font-semibold text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200',
          ]">
          <span class="truncate">{{ option.label }}</span>
          <svg v-if="option.value === modelValue" class="w-4 h-4 flex-shrink-0 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
          </svg>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  label: { type: String, required: true },
  modelValue: { type: String, default: '' },
  // [{ value, label }]. The first option is the "everything" one, which is not counted as a filter.
  options: { type: Array, required: true },
  // The value that means "no filter". Leave unset to treat the first option as that.
  neutral: { type: String, default: undefined },
})
const emit = defineEmits(['update:modelValue'])

const id = `filter-${Math.random().toString(36).slice(2, 8)}`
const root = ref(null)
const trigger = ref(null)
const menu = ref(null)
const open = ref(false)
const highlighted = ref(0)

const selectedIndex = computed(() => Math.max(0, props.options.findIndex((option) => option.value === props.modelValue)))
const selectedLabel = computed(() => props.options[selectedIndex.value]?.label ?? props.label)
const neutralValue = computed(() => props.neutral ?? props.options[0]?.value)
const active = computed(() => props.modelValue !== neutralValue.value)

function show() {
  open.value = true
  highlighted.value = selectedIndex.value
  nextTick(() => menu.value?.focus())
}

function hide({ refocus = true } = {}) {
  open.value = false
  if (refocus) trigger.value?.focus()
}

const toggle = () => (open.value ? hide() : show())

function choose(option) {
  emit('update:modelValue', option.value)
  hide()
}

function move(step) {
  const count = props.options.length
  highlighted.value = (highlighted.value + step + count) % count
  nextTick(() => document.getElementById(`${id}-option-${highlighted.value}`)?.scrollIntoView({ block: 'nearest' }))
}

function onTriggerKey(event) {
  if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault()
    show()
  }
}

function onMenuKey(event) {
  const handlers = {
    ArrowDown: () => move(1),
    ArrowUp: () => move(-1),
    Home: () => { highlighted.value = 0 },
    End: () => { highlighted.value = props.options.length - 1 },
    Enter: () => choose(props.options[highlighted.value]),
    ' ': () => choose(props.options[highlighted.value]),
    Escape: () => hide(),
    Tab: () => hide({ refocus: false }),
  }
  const handler = handlers[event.key]
  if (!handler) return
  event.preventDefault()
  handler()
}

// A click anywhere else closes the menu.
function onOutside(event) {
  if (root.value && !root.value.contains(event.target)) hide({ refocus: false })
}

watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', onOutside)
  else document.removeEventListener('pointerdown', onOutside)
})
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside))
</script>
